import { useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Upload,
  FileJson,
  Watch,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  Camera,
  Scale,
  Dumbbell,
  X,
  Activity,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "wouter";
import { format } from "date-fns";

// ─── JSON Backup Importer ─────────────────────────────────────────────────────

function JsonImporter() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<{
    sessions: number;
    weights: number;
    measurements: number;
    splits: number;
    templates: number;
    exercises: number;
    errors: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importMutation = trpc.import.fromJson.useMutation({
    onSuccess: (data) => {
      setResult(data);
      const total = data.sessions + data.weights + data.measurements + data.splits + data.templates + data.exercises;
      if (data.errors.length === 0) {
        toast.success(`Import complete! ${total} records imported.`);
      } else {
        toast.warning(`Import finished with ${data.errors.length} error(s). ${total} records imported.`);
      }
    },
    onError: (err) => {
      toast.error(`Import failed: ${err.message}`);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    const text = await file.text();
    importMutation.mutate({ jsonData: text });
  };

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-primary/10 rounded-sm flex items-center justify-center">
            <FileJson className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="font-mono text-sm tracking-wider">JSON BACKUP IMPORT</CardTitle>
            <CardDescription className="text-xs font-mono">
              Restore your old Forge Telemetry localStorage backup
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-2 border-dashed border-border rounded-sm p-6 text-center space-y-3 hover:border-primary/50 transition-colors">
          <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
          <div>
            <p className="text-sm font-mono text-muted-foreground">
              {file ? (
                <span className="text-foreground font-semibold">{file.name}</span>
              ) : (
                "Select your forge_workout_backup_*.json file"
              )}
            </p>
            {file && (
              <p className="text-xs text-muted-foreground mt-1">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="font-mono text-xs"
          >
            {file ? "CHANGE FILE" : "SELECT FILE"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {file && !result && (
          <Button
            onClick={handleImport}
            disabled={importMutation.isPending}
            className="w-full font-mono text-xs tracking-wider"
          >
            {importMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                IMPORTING...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                IMPORT BACKUP
              </>
            )}
          </Button>
        )}

        {result && (
          <div className="space-y-3">
            <div className="bg-primary/5 border border-primary/20 rounded-sm p-4 space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span className="font-mono text-xs font-bold text-primary tracking-wider">IMPORT COMPLETE</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs font-mono">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{result.sessions}</div>
                  <div className="text-muted-foreground">Sessions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{result.weights}</div>
                  <div className="text-muted-foreground">Weight Logs</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{result.measurements}</div>
                  <div className="text-muted-foreground">Measurements</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{result.splits}</div>
                  <div className="text-muted-foreground">Splits</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{result.templates}</div>
                  <div className="text-muted-foreground">Templates</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{result.exercises}</div>
                  <div className="text-muted-foreground">Custom Exs</div>
                </div>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-sm p-3 space-y-1">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <span className="font-mono text-xs font-bold text-destructive">{result.errors.length} ERRORS</span>
                </div>
                {result.errors.slice(0, 5).map((err, i) => (
                  <p key={i} className="text-xs font-mono text-destructive/80">{err}</p>
                ))}
                {result.errors.length > 5 && (
                  <p className="text-xs font-mono text-muted-foreground">...and {result.errors.length - 5} more</p>
                )}
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => { setFile(null); setResult(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              className="w-full font-mono text-xs"
            >
              IMPORT ANOTHER FILE
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Apple Watch Screenshot Importer ─────────────────────────────────────────

interface ExtractedWatchData {
  workoutType: string | null;
  date: string | null;
  durationMinutes: number | null;
  calories: number | null;
  avgHeartRate: number | null;
  maxHeartRate: number | null;
  distanceMiles: number | null;
  steps: number | null;
  bodyWeight: number | null;
  bodyFat: number | null;
  notes: string | null;
}

function WatchScreenshotImporter() {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState("image/png");
  const [extracted, setExtracted] = useState<ExtractedWatchData | null>(null);
  const [editedData, setEditedData] = useState<ExtractedWatchData | null>(null);
  const [savedItems, setSavedItems] = useState<string[] | null>(null);
  const [dataType, setDataType] = useState<"cardio" | "strength">("cardio");
  const [strengthSessionId, setStrengthSessionId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load completed sessions for the strength biometrics picker
  const { data: allSessions = [] } = trpc.sessions.list.useQuery();
  const completedSessions = allSessions
    .filter((s) => s.completedAt && !s.isLive)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 30);

  const parseMutation = trpc.import.parseWatchScreenshot.useMutation({
    onSuccess: (data) => {
      setExtracted(data.extracted);
      setEditedData({ ...data.extracted });
      toast.success("Screenshot analyzed! Review the extracted data below.");
    },
    onError: (err) => {
      toast.error(`Analysis failed: ${err.message}`);
    },
  });

  const saveMutation = trpc.import.saveWatchData.useMutation({
    onSuccess: (data) => {
      setSavedItems(data.saved);
      toast.success(`Saved: ${data.saved.join(", ")}`);
    },
    onError: (err) => {
      toast.error(`Save failed: ${err.message}`);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMimeType(f.type || "image/png");
    setExtracted(null);
    setEditedData(null);
    setSavedItems(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setImagePreview(result);
      // Strip the data:image/...;base64, prefix
      const base64 = result.split(",")[1];
      setImageBase64(base64);
    };
    reader.readAsDataURL(f);
  };

  const handleAnalyze = () => {
    if (!imageBase64) return;
    parseMutation.mutate({ imageBase64, mimeType });
  };

  const handleSave = () => {
    if (!editedData) return;
    const date = editedData.date ?? format(new Date(), "yyyy-MM-dd");
    saveMutation.mutate({
      date,
      dataType,
      strengthSessionId: dataType === "strength" ? (strengthSessionId ?? undefined) : undefined,
      bodyWeight: editedData.bodyWeight,
      bodyFat: editedData.bodyFat,
      workoutType: editedData.workoutType,
      durationMinutes: editedData.durationMinutes,
      calories: editedData.calories,
      avgHeartRate: editedData.avgHeartRate,
      maxHeartRate: editedData.maxHeartRate,
      distanceMiles: editedData.distanceMiles,
      notes: editedData.notes,
    });
  };

  const updateField = (field: keyof ExtractedWatchData, value: string) => {
    if (!editedData) return;
    const numFields = ["durationMinutes", "calories", "avgHeartRate", "maxHeartRate", "distanceMiles", "steps", "bodyWeight", "bodyFat"];
    setEditedData({
      ...editedData,
      [field]: numFields.includes(field) ? (value === "" ? null : parseFloat(value)) : (value === "" ? null : value),
    });
  };

  const clearImage = () => {
    setImagePreview(null);
    setImageBase64(null);
    setExtracted(null);
    setEditedData(null);
    setSavedItems(null);
    setStrengthSessionId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-primary/10 rounded-sm flex items-center justify-center">
            <Watch className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="font-mono text-sm tracking-wider">APPLE WATCH IMPORT</CardTitle>
            <CardDescription className="text-xs font-mono">
              Upload a screenshot — AI extracts workout & health data
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Image upload area */}
        {!imagePreview ? (
          <div
            className="border-2 border-dashed border-border rounded-sm p-6 text-center space-y-3 hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <Camera className="h-8 w-8 mx-auto text-muted-foreground" />
            <div>
              <p className="text-sm font-mono text-muted-foreground">
                Tap to upload Apple Watch / Fitness screenshot
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, HEIC — workout summary, activity rings, health metrics
              </p>
            </div>
            <Button variant="outline" size="sm" className="font-mono text-xs pointer-events-none">
              SELECT SCREENSHOT
            </Button>
          </div>
        ) : (
          <div className="relative">
            <img
              src={imagePreview}
              alt="Watch screenshot"
              className="w-full max-h-64 object-contain rounded-sm border border-border bg-black/5"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={clearImage}
              className="absolute top-2 right-2 h-7 w-7 bg-background/80 hover:bg-background"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Analyze button */}
        {imageBase64 && !extracted && (
          <Button
            onClick={handleAnalyze}
            disabled={parseMutation.isPending}
            className="w-full font-mono text-xs tracking-wider"
          >
            {parseMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ANALYZING WITH AI...
              </>
            ) : (
              <>
                <Watch className="h-4 w-4 mr-2" />
                ANALYZE SCREENSHOT
              </>
            )}
          </Button>
        )}

        {/* Extracted data editor */}
        {editedData && !savedItems && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <span className="font-mono text-xs font-bold text-primary tracking-wider">DATA EXTRACTED — REVIEW & CONFIRM</span>
            </div>

            {/* Data type selector */}
            <div className="space-y-2">
              <Label className="text-xs font-mono text-muted-foreground">WHAT IS THIS WATCH DATA FOR?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { setDataType("cardio"); setStrengthSessionId(null); }}
                  className={`flex items-center gap-2 p-3 rounded-sm border text-xs font-mono transition-colors ${
                    dataType === "cardio"
                      ? "border-red-400/60 bg-red-400/10 text-red-400"
                      : "border-border text-muted-foreground hover:border-border/80"
                  }`}
                >
                  <Activity className="h-4 w-4" />
                  CARDIO SESSION
                </button>
                <button
                  type="button"
                  onClick={() => setDataType("strength")}
                  className={`flex items-center gap-2 p-3 rounded-sm border text-xs font-mono transition-colors ${
                    dataType === "strength"
                      ? "border-primary/60 bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-border/80"
                  }`}
                >
                  <Dumbbell className="h-4 w-4" />
                  STRENGTH BIOMETRICS
                </button>
              </div>
              {dataType === "strength" && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono text-muted-foreground">ATTACH TO STRENGTH SESSION (optional)</Label>
                  <Select value={strengthSessionId ?? ""} onValueChange={(v) => setStrengthSessionId(v || null)}>
                    <SelectTrigger className="h-8 text-xs font-mono">
                      <SelectValue placeholder="Select a session..." />
                    </SelectTrigger>
                    <SelectContent>
                      {completedSessions.map((s) => (
                        <SelectItem key={s.id} value={s.id} className="text-xs font-mono">
                          {s.date} — {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground font-mono">
                    Calories &amp; HR will appear on the selected session's summary card
                  </p>
                </div>
              )}
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-mono text-muted-foreground">DATE</Label>
                <Input
                  type="date"
                  value={editedData.date ?? ""}
                  onChange={(e) => updateField("date", e.target.value)}
                  className="h-8 text-xs font-mono"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-mono text-muted-foreground">WORKOUT TYPE</Label>
                <Input
                  value={editedData.workoutType ?? ""}
                  onChange={(e) => updateField("workoutType", e.target.value)}
                  placeholder="e.g. Outdoor Run"
                  className="h-8 text-xs font-mono"
                />
              </div>
            </div>

            <Separator />

            {/* Body metrics section */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Scale className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">Body Metrics</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-mono text-muted-foreground">BODY WEIGHT (lbs)</Label>
                  <Input
                    type="number"
                    value={editedData.bodyWeight ?? ""}
                    onChange={(e) => updateField("bodyWeight", e.target.value)}
                    placeholder="e.g. 185"
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-mono text-muted-foreground">BODY FAT %</Label>
                  <Input
                    type="number"
                    value={editedData.bodyFat ?? ""}
                    onChange={(e) => updateField("bodyFat", e.target.value)}
                    placeholder="e.g. 15.2"
                    className="h-8 text-xs font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Workout metrics section */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Dumbbell className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">Workout Metrics</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-mono text-muted-foreground">DURATION (min)</Label>
                  <Input
                    type="number"
                    value={editedData.durationMinutes ?? ""}
                    onChange={(e) => updateField("durationMinutes", e.target.value)}
                    placeholder="e.g. 45"
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-mono text-muted-foreground">CALORIES</Label>
                  <Input
                    type="number"
                    value={editedData.calories ?? ""}
                    onChange={(e) => updateField("calories", e.target.value)}
                    placeholder="e.g. 320"
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-mono text-muted-foreground">AVG HEART RATE</Label>
                  <Input
                    type="number"
                    value={editedData.avgHeartRate ?? ""}
                    onChange={(e) => updateField("avgHeartRate", e.target.value)}
                    placeholder="e.g. 142"
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-mono text-muted-foreground">MAX HEART RATE</Label>
                  <Input
                    type="number"
                    value={editedData.maxHeartRate ?? ""}
                    onChange={(e) => updateField("maxHeartRate", e.target.value)}
                    placeholder="e.g. 178"
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-mono text-muted-foreground">DISTANCE (mi)</Label>
                  <Input
                    type="number"
                    value={editedData.distanceMiles ?? ""}
                    onChange={(e) => updateField("distanceMiles", e.target.value)}
                    placeholder="e.g. 3.1"
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-mono text-muted-foreground">NOTES</Label>
                  <Input
                    value={editedData.notes ?? ""}
                    onChange={(e) => updateField("notes", e.target.value)}
                    placeholder="Optional"
                    className="h-8 text-xs font-mono"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                className="flex-1 font-mono text-xs tracking-wider"
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    SAVING...
                  </>
                ) : (
                  "SAVE TO FORGE"
                )}
              </Button>
              <Button
                variant="outline"
                onClick={clearImage}
                className="font-mono text-xs"
              >
                CANCEL
              </Button>
            </div>
          </div>
        )}

        {/* Success state */}
        {savedItems && (
          <div className="space-y-3">
            <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <span className="font-mono text-xs font-bold text-primary tracking-wider">SAVED TO FORGE</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {savedItems.map((item) => (
                  <Badge key={item} variant="secondary" className="font-mono text-xs capitalize">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearImage}
              className="w-full font-mono text-xs"
            >
              IMPORT ANOTHER SCREENSHOT
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main Import Page ─────────────────────────────────────────────────────────

export default function ImportData() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/60 backdrop-blur-md sticky top-0 z-40" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="container py-3 flex items-center gap-3">
          <Link href="/">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="font-mono text-base font-bold tracking-wider text-primary">FORGE // DATA IMPORT</h1>
            <p className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest">
              Restore backups &amp; import Apple Watch data
            </p>
          </div>
        </div>
      </header>

      <main className="container py-6 space-y-6 max-w-2xl">
        {/* Info banner */}
        <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
          <p className="text-xs font-mono text-muted-foreground leading-relaxed">
            <span className="text-primary font-bold">SAFE TO RE-IMPORT:</span> The importer uses upsert logic — importing the same backup file multiple times will not create duplicates. Existing records are updated in place, new records are added.
          </p>
        </div>

        {/* JSON Backup Import */}
        <JsonImporter />

        <div className="flex items-center gap-3">
          <Separator className="flex-1" />
          <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest">OR</span>
          <Separator className="flex-1" />
        </div>

        {/* Apple Watch Screenshot Import */}
        <WatchScreenshotImporter />
      </main>
    </div>
  );
}
